import type { AdjustmentKind } from '../model/types';
import { applyAdjustment } from './adjust';
import { blurBuffer, sharpenBuffer } from './blur';
import type { PixelBuffer } from './buffer';
import { applyLut, curveLut, levelsLut } from './curves';
import { noiseBuffer, pixelateBuffer, vignetteBuffer } from './effects';
import { type AdjustParams, blurRadius, clampParams, neutralParams, pixelBlockSize } from './spec';

/**
 * The one entry point `io/` calls.
 *
 * It takes the whole parameter set, not a number: brightness has one slider, levels
 * has three and curves has a drawn line, and a preview must be able to re-run any of
 * them from the same call. Pointwise kinds mutate the buffer; convolution kinds
 * return a new one, because they need the untouched source to diff against.
 * Callers must use the returned buffer either way — that is why nothing here
 * returns void.
 */
export function applyFilter(
  buffer: PixelBuffer,
  kind: AdjustmentKind,
  params: AdjustParams = neutralParams(kind),
): PixelBuffer {
  // Clamped here, not only at the UI boundary: this is the public core entry
  // point, and an out-of-range parameter must degrade to the nearest honest
  // result rather than, say, a contrast factor that crossed its pole and
  // printed an inverted image.
  const { sliders, curve } = clampParams(kind, params);
  switch (kind) {
    case 'blur':
      return blurBuffer(buffer, blurRadius(sliders.value ?? 0));
    case 'sharpen':
      return sharpenBuffer(buffer, (sliders.value ?? 0) * 2, 1);
    case 'pixelate':
      return pixelateBuffer(buffer, pixelBlockSize(sliders.value ?? 0));
    case 'noise':
      noiseBuffer(buffer, sliders.value ?? 0);
      return buffer;
    case 'vignette':
      vignetteBuffer(buffer, sliders.value ?? 0);
      return buffer;
    case 'levels':
      applyLut(buffer, levelsLut(sliders.black ?? 0, sliders.white ?? 255, sliders.gamma ?? 1));
      return buffer;
    case 'curves':
      applyLut(buffer, curveLut(curve));
      return buffer;
    default:
      applyAdjustment(buffer, kind, sliders.value ?? 0);
      return buffer;
  }
}

export {
  applyAdjustment,
  applyBrightness,
  applyContrast,
  applyExposure,
  applyGrayscale,
  applyHue,
  applyInvert,
  applySaturation,
  applySepia,
} from './adjust';
export { blurBuffer, sharpenBuffer } from './blur';
export type { PixelBuffer } from './buffer';
export { clampByte, cloneBuffer, luma, premultiply, unpremultiply } from './buffer';
export {
  addOrMoveCurvePoint,
  applyLut,
  type CurvePoint,
  curveIsIdentity,
  curveLut,
  identityCurve,
  levelsLut,
  moveCurvePoint,
  nearestCurvePoint,
  normaliseCurve,
  removeCurvePoint,
} from './curves';
export { noiseBuffer, pixelateBuffer, vignetteBuffer } from './effects';
export type { AdjustmentSpec, AdjustParams, ControlSpec } from './spec';
export {
  ADJUSTMENT_BY_KIND,
  ADJUSTMENTS,
  blurRadius,
  clampParams,
  controlsOf,
  formatControl,
  isCurveKind,
  neutralParams,
  neutralValue,
  normalisedValue,
  paramsAreNeutral,
  pixelBlockSize,
  uiRange,
  uiValue,
} from './spec';
